/**
 * Реестр языков блока кода вопроса (Фаза 16, B2) — единый source-of-truth
 * фронтенда: код whitelist → ленивый динамический импорт грамматики
 * highlight.js + человекочитаемая метка.
 *
 * Гранулярная схема чанков (прецедент — ленивые каталоги i18n en/cn,
 * [i18n/index.ts](./i18n/index.ts:59)): core highlight.js — свой ленивый чанк
 * (~12–15 КБ gz), каждая грамматика — отдельный мини-чанк (~1–3.5 КБ gz)
 * через `import('highlight.js/lib/languages/<lang>')`. Зависимости грамматик
 * (ts→js, cpp→c, php→markup/xml) Vite разрешает сам через статические
 * импорты внутри языковых модулей. Все чанки immutable-кэшируются (хэш в
 * имени файла). Никакого CDN и дефолтного бандла highlight.js — только
 * `highlight.js/lib/core` + отдельные `lib/languages/*`.
 *
 * Паритет whitelist: состав 14 кодов обязан совпадать с серверным каноном
 * `game.CodeLangs` ([question.go](../../server/internal/game/question.go))
 * и списком в docs/dev/realtime-protocol.md §8.7. Рассинхрон ловит тест
 * паритета [languages.test.ts](./languages.test.ts) (список захардкожен
 * в тесте) и серверная валидация codeLang при сохранении квиза.
 *
 * Используется загрузчиком `ensureLanguages` (здесь же) и селектором языка
 * редактора (B4). plaintext — псевдо-язык «без подсветки»: грамматики не
 * имеет, метка локализуется i18n-ключом `question.code.langPlaintext`
 * (остальные метки — имена собственные, локали не требуют).
 */
import type { HLJSApi, LanguageFn } from 'highlight.js';

/** Запись реестра: код whitelist + метка + ленивый импорт грамматики. */
export interface CodeLanguageEntry {
  /** Код языка из whitelist (wire `question.codeLang` / state `langs`). */
  code: string;
  /**
   * Человекочитаемая метка (имя собственное: Go, Python, C++…). Для
   * plaintext пуста — метка локали-зависима, берётся через `labelKey`.
   */
  label: string;
  /** i18n-ключ метки (только plaintext: «Обычный текст» / «Plain text» / «纯文本»). */
  labelKey?: string;
  /**
   * Ленивый импорт грамматики hljs (свой чанк на язык). Отсутствует у
   * plaintext — подсветка не нужна, блок рендерится как есть.
   */
  load?: () => Promise<{ default: LanguageFn }>;
}

/** Псевдо-язык «без подсветки» — дефолт/фолбэк (канон: game.CodeLangPlaintext). */
export const CODE_LANG_PLAINTEXT = 'plaintext';

/**
 * Реестр — ровно 14 записей whitelist (порядок = серверный game.CodeLangs).
 * Метки — имена собственные как есть; plaintext — через i18n-ключ.
 */
export const CODE_LANGUAGES: readonly CodeLanguageEntry[] = [
  {
    code: CODE_LANG_PLAINTEXT,
    label: '',
    labelKey: 'question.code.langPlaintext',
  },
  {
    code: 'javascript',
    label: 'JavaScript',
    load: () => import('highlight.js/lib/languages/javascript'),
  },
  {
    code: 'typescript',
    label: 'TypeScript',
    load: () => import('highlight.js/lib/languages/typescript'),
  },
  {
    code: 'go',
    label: 'Go',
    load: () => import('highlight.js/lib/languages/go'),
  },
  {
    code: 'python',
    label: 'Python',
    load: () => import('highlight.js/lib/languages/python'),
  },
  {
    code: 'sql',
    label: 'SQL',
    load: () => import('highlight.js/lib/languages/sql'),
  },
  {
    code: 'bash',
    label: 'Bash',
    load: () => import('highlight.js/lib/languages/bash'),
  },
  {
    code: 'java',
    label: 'Java',
    load: () => import('highlight.js/lib/languages/java'),
  },
  {
    code: 'c',
    label: 'C',
    load: () => import('highlight.js/lib/languages/c'),
  },
  {
    code: 'cpp',
    label: 'C++',
    load: () => import('highlight.js/lib/languages/cpp'),
  },
  {
    code: 'json',
    label: 'JSON',
    load: () => import('highlight.js/lib/languages/json'),
  },
  {
    code: 'xml',
    label: 'XML',
    load: () => import('highlight.js/lib/languages/xml'),
  },
  {
    code: 'php',
    label: 'PHP',
    load: () => import('highlight.js/lib/languages/php'),
  },
  {
    code: 'css',
    label: 'CSS',
    load: () => import('highlight.js/lib/languages/css'),
  },
];

/** Коды реестра в порядке записи (тест паритета сверяет с whitelist). */
export const CODE_LANGUAGE_CODES: readonly string[] = CODE_LANGUAGES.map((e) => e.code);

/** Запись реестра по коду; undefined для незнакомого языка. */
export function findCodeLanguage(code: string): CodeLanguageEntry | undefined {
  return CODE_LANGUAGES.find((e) => e.code === code);
}

/** true, если code — элемент whitelist реестра. */
export function isKnownCodeLang(code: string): boolean {
  return findCodeLanguage(code) !== undefined;
}

// ─── Загрузчик ensureLanguages (B2) ───────────────────────────────────────────

/**
 * Кэш core-инстанса hljs: грузится один раз своим ленивым чанком, повторные
 * вызовы ensureLanguages переиспользуют (регистрация грамматик — на том же
 * синглтоне). null до первой успешной загрузки.
 */
let hljsCore: HLJSApi | null = null;

/** Кэш core-промиса: параллельные вызовы не дублируют динамический import. */
let hljsCorePromise: Promise<HLJSApi> | null = null;

/** Успешно зарегистрированные языки (идемпотентность: не регистрируем дважды). */
const registered = new Set<string>();

/** Грузит core highlight.js один раз (свой ленивый Vite-чанк). */
function loadCore(): Promise<HLJSApi> {
  hljsCorePromise ??= import('highlight.js/lib/core').then((m) => {
    hljsCore = m.default;
    return m.default;
  });
  return hljsCorePromise;
}

/**
 * Гарантирует, что грамматики перечисленных языков загружены и
 * зарегистрированы на core-инстансе highlight.js; возвращает инстанс (или
 * null, если core-чанк не загрузился — тихий фолбэк plaintext).
 *
 * Свойства (план B2):
 * - грузит core + только ОТСУТСТВУЮЩИЕ грамматики, параллельно;
 * - идемпотентна: повторный вызов с уже загруженными языками — no-op
 *   (быстрый путь без нагрузки);
 * - ошибки чанков НЕ роняют вызов: упавший язык просто не регистрируется
 *   (не попадает в кэш — повторный вызов может ретраить, например после
 *   восстановления сети), блок кода остаётся читаемым plaintext.
 * - незнакомые коды и plaintext тихо пропускаются (грамматика не нужна).
 *
 * Точка вызова — вход в сессию по `state.langs` (PlayerGame/HostSession):
 * событийно, без блокировки рендера; CodeBlock вызывает отдельно для своего
 * языка (соло-режим/редактор грузят по требованию).
 */
export async function ensureLanguages(langs: readonly string[]): Promise<HLJSApi | null> {
  const need = [...new Set(langs)].filter((code) => {
    // plaintext и дубли отсеяны; незнакомые коды — не ошибка (forward-compat).
    const entry = findCodeLanguage(code);
    return entry !== undefined && entry.load !== undefined && !registered.has(code);
  });
  // Быстрый путь идемпотентности: всё уже зарегистрировано — без промисов.
  if (need.length === 0 && hljsCore !== null) return hljsCore;

  let hljs: HLJSApi;
  try {
    hljs = await loadCore();
  } catch {
    // core-чанк не загрузился (офлайн и т.п.) — весь вызов деградирует в
    // plaintext; кэш-промис сбрасываем, следующий вызов ретраит.
    hljsCorePromise = null;
    return null;
  }

  await Promise.all(
    need.map(async (code) => {
      const entry = findCodeLanguage(code);
      if (!entry?.load || registered.has(code)) return;
      try {
        const mod = await entry.load();
        hljs.registerLanguage(code, mod.default);
        registered.add(code);
      } catch {
        // Чанк грамматики не загрузился — тихо: язык остаётся plaintext.
        // В кэш не попадает → повторный ensureLanguages ретраит.
      }
    }),
  );
  return hljs;
}

/** Сброс кэшей загрузчика (только для тестов). */
export function resetLanguageCacheForTests(): void {
  hljsCore = null;
  hljsCorePromise = null;
  registered.clear();
}
